import { useState, useEffect } from "react";
import { X, Link2, Copy, Calendar, Lock, Download, MessageSquare, CheckCircle2, Loader2, Check, Eye, Layers } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";

export default function ShareModal({ target, onClose, onCreated }) {
  // target: { kind: 'asset'|'folder'|'collection', id, title }
  const isCollection = target.kind === "collection";
  const isFolder = target.kind === "folder";
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [allowDownload, setAllowDownload] = useState(false);
  // Collections are portfolio handoffs — Review mode is always off, no toggle shown.
  const [reviewMode, setReviewMode] = useState(!isCollection);
  const [allowComments, setAllowComments] = useState(true);
  const [allowApproval, setAllowApproval] = useState(true);
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdUrl, setCreatedUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/shares", {
        kind: target.kind,
        target_id: target.id,
        password: usePassword && password ? password : null,
        expires_in_days: expiresInDays === 0 ? null : expiresInDays,
        allow_download: allowDownload,
        allow_comments: reviewMode ? allowComments : false,
        allow_approval: reviewMode ? allowApproval : false,
        include_subfolders: isFolder ? includeSubfolders : true,
        title: title || null,
      });
      setCreatedUrl(data.url);
      onCreated?.(data);
    } catch (ex) {
      setError(ex.response?.data?.detail || "Failed to create share link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(createdUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid={TID.shareDialog}
        className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/60">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[var(--brand-accent)]" />
            <h2 className="font-heading text-lg font-semibold tracking-tight">
              {createdUrl ? "Share link ready" : "Create share link"}
            </h2>
          </div>
          <button data-testid={TID.shareCloseBtn} onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {createdUrl ? (
          <div className="p-6 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Sharing &ldquo;{target.title}&rdquo;
              </div>
              <div className="flex items-center gap-2 bg-black/50 border border-zinc-800 rounded-md p-2">
                <input
                  data-testid={TID.shareLinkUrl}
                  readOnly
                  value={createdUrl}
                  className="flex-1 bg-transparent text-xs text-zinc-200 px-2 focus:outline-none font-mono"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  data-testid={TID.shareCopyBtn}
                  onClick={copy}
                  className="px-3 py-1.5 bg-[var(--brand-accent)] text-black text-xs font-semibold rounded hover:bg-[var(--brand-accent)]/90 transition flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
            <a
              href={createdUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-xs text-zinc-400 hover:text-[var(--brand-accent)] underline"
            >
              Open share page in new tab →
            </a>
            <button
              onClick={onClose}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md py-2 text-sm text-zinc-300 hover:border-zinc-700 transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={create} className="p-6 space-y-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Sharing {target.kind === "asset" ? "video" : target.kind}: <span className="text-zinc-300 normal-case tracking-normal">{target.title}</span>
            </div>
            {isCollection && (
              <div className="bg-[var(--brand-accent)]/5 border border-[var(--brand-accent)]/20 rounded-lg px-3 py-2 text-[11px] text-[var(--brand-accent)] flex items-start gap-2">
                <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Portfolio mode — viewers see a clean, full-width player. No comments or approvals are collected.</span>
              </div>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Link label (optional)</label>
              <input
                data-testid={TID.shareTitleInput}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Final cut review for Acme"
                className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60"
              />
            </div>

            <Toggle
              testid={TID.shareUsePassword}
              icon={Lock}
              label="Require password"
              hint="Client must enter a password to view"
              checked={usePassword}
              onChange={setUsePassword}
            />
            {usePassword && (
              <input
                data-testid={TID.sharePasswordInput}
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Set a password"
                minLength={4}
                required
                className="w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60 -mt-2"
              />
            )}

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Link expires
              </label>
              <select
                data-testid={TID.shareExpirySelect}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10))}
                className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60"
              >
                <option value={7}>In 7 days</option>
                <option value={14}>In 14 days</option>
                <option value={30}>In 30 days</option>
                <option value={90}>In 90 days</option>
                <option value={0}>Never expires</option>
              </select>
            </div>

            <div className="space-y-3">
              {!isCollection && (
                <div className="bg-black/30 border border-zinc-800/80 rounded-lg p-3 space-y-3">
                  <Toggle
                    testid={TID.shareReviewMode}
                    icon={Eye}
                  label="Review mode"
                  hint={reviewMode ? "Clients can comment and approve" : "Watch-only — no feedback collected"}
                  checked={reviewMode}
                  onChange={setReviewMode}
                />
                {reviewMode && (
                  <div className="pl-3 border-l border-zinc-800/80 space-y-3">
                    <Toggle
                      testid={TID.shareAllowComments}
                      icon={MessageSquare}
                      label="Allow comments"
                      hint="Clients can leave timestamped comments"
                      checked={allowComments}
                      onChange={setAllowComments}
                    />
                    <Toggle
                      testid={TID.shareAllowApproval}
                      icon={CheckCircle2}
                      label="Allow approve / reject"
                      hint="Clients can approve or request changes"
                      checked={allowApproval}
                      onChange={setAllowApproval}
                    />
                  </div>
                )}
                {!reviewMode && (
                  <div className="pl-3 border-l border-zinc-800/80 text-[11px] text-zinc-500 leading-relaxed">
                    A clean, full-width player will be shown to the recipient. The feedback sidebar, comments and approval buttons are hidden.
                  </div>
                )}
                </div>
              )}
              <Toggle
                testid={TID.shareAllowDownload}
                icon={Download}
                label="Allow download"
                hint="Requires MP4 Fallback in Bunny library settings"
                checked={allowDownload}
                onChange={setAllowDownload}
              />
              {isFolder && (
                <Toggle
                  testid="share-include-subfolders"
                  icon={Layers}
                  label="Include subfolders"
                  hint="When on, videos in nested subfolders are included in the share."
                  checked={includeSubfolders}
                  onChange={setIncludeSubfolders}
                />
              )}
            </div>

            {error && <div className="text-xs text-red-400">{error}</div>}

            <button
              data-testid={TID.shareCreateBtn}
              type="submit"
              disabled={busy}
              className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2.5 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              {busy ? "Creating..." : "Create share link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Toggle({ icon: Icon, label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <button
        type="button"
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`shrink-0 w-9 h-5 rounded-full transition relative ${checked ? "bg-[var(--brand-accent)]" : "bg-zinc-800"}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </button>
      <Icon className="w-3.5 h-3.5 text-zinc-500" />
      <div className="flex-1">
        <div className="text-sm text-zinc-200">{label}</div>
        {hint && <div className="text-[10px] text-zinc-500">{hint}</div>}
      </div>
    </label>
  );
}
